import { useCallback, useEffect, useRef, useState } from 'react';
import BZAPIService from '../../../services/BZAPIService';

export const POLL_INTERVAL_MS = 3000;

/**
 * Drives the async CMDB discovery scan: start the job, poll until it leaves
 * RUNNING. Mirrors useInventoryFetch but for product enumeration (mode=discover)
 * and is kept separate so the per-product fetch flow is untouched.
 */
export default function useInventoryDiscoverScan() {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      runIdRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    runIdRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    runIdRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('idle');
    setResult(null);
    setError(null);
  }, []);

  const start = useCallback(async vendorId => {
    const runId = (runIdRef.current += 1);
    const isStale = () => cancelledRef.current || runIdRef.current !== runId;

    if (timerRef.current) clearTimeout(timerRef.current);
    cancelledRef.current = false;
    setError(null);
    setResult(null);
    setStatus('scanning');

    let jobId;
    try {
      const started = await BZAPIService.bsaInvStartDiscoverScan(vendorId);
      jobId = started && started.jobId;
      if (!jobId) throw new Error('no job id');
    } catch (e) {
      if (!isStale()) {
        setError('Could not start the scan. Please try again.');
        setStatus('failed');
      }
      return;
    }

    const poll = async () => {
      if (isStale()) return;
      let job;
      try {
        job = await BZAPIService.bsaInvGetFetchJob(jobId);
      } catch (e) {
        if (!isStale()) {
          setError('Lost contact with the scan. Please try again.');
          setStatus('failed');
        }
        return;
      }
      if (isStale()) return;

      if (job && job.status === 'RUNNING') {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }
      if (job && job.status === 'SUCCEEDED') {
        setResult(job.result || { products: [] });
        setStatus('succeeded');
        return;
      }
      setError((job && job.error) || 'The scan failed.');
      setStatus('failed');
    };

    await poll();
  }, []);

  return { status, result, error, start, cancel, reset };
}
